import { QueryKeys } from '@/enums';
import { createQueryKey } from '@/helpers/create-querykey';
import { ILocation, LocationData, UseGetAllLocationListOptions } from '@/interfaces/location';
import { IAutoCompleteStoreList, IStoreLists } from '@/interfaces/store';
import {
  getAllExpressZones,
  getAllLocationLists,
  getAutoCompleteStoreLists,
  getLocationLists,
  getStoreForFeedbackLists,
  getStoreLists,
} from '@/services/location.service';
import { getAllTicketLists } from '@/services/ticket.service';
import { useQuery } from '@tanstack/react-query';

export const useGetDivisionAddressList = () => {
  return useQuery<ILocation[]>({
    queryKey: [QueryKeys.divisionLocation],
    queryFn: async () => {
      const locationList = await getLocationLists();
      return locationList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};
export const useGetDistrictAddressList = (parentId?: string) => {
  return useQuery<ILocation[]>({
    queryKey: [QueryKeys.locationLists + parentId],
    queryFn: async () => {
      const locationList = await getLocationLists(parentId);
      return locationList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!parentId,
  });
};
export const useGetThanaAddressList = (parentId?: string) => {
  return useQuery<ILocation[]>({
    queryKey: [QueryKeys.locationLists + parentId],
    queryFn: async () => {
      const locationList = await getLocationLists(parentId);
      return locationList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!parentId,
  });
};

export const useGetStoreList = (
  divisionId?: string,
  districtId?: string,
  thanaId?: string,
  locationName?: string,
  latitude?: number,
  longitude?: number,
  enabled: boolean = true,
  IsBOPIS?: boolean | null,
  IsShowMapPage?: boolean,
  accessToken?: string,
) => {
  const queryKey = createQueryKey(QueryKeys.storeLists, locationName, divisionId, districtId, thanaId, latitude, longitude, IsBOPIS, accessToken);

  return useQuery<IStoreLists[]>({
    queryKey,
    queryFn: async () => {
      const storeList = await getStoreLists(locationName, divisionId, districtId, thanaId, latitude, longitude, IsBOPIS, IsShowMapPage, accessToken);
      return storeList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enabled,
    placeholderData: (previousData) => previousData,
  });
};

export const useGetStoreListForFeedback = (
  locationId?: string,
  locationName?: string,
  latitude?: number,
  longitude?: number,
  getAll?: boolean,
  descOrder?: boolean,
) => {
  let tempQueryKey = [QueryKeys.storeLists] as string[];
  if (locationName) {
    tempQueryKey.push(locationName);
  }
  if (locationId) {
    tempQueryKey.push(locationId);
  }
  if (latitude) {
    tempQueryKey.push(latitude.toString());
  }
  if (longitude) {
    tempQueryKey.push(longitude.toString());
  }
  if (getAll) {
    tempQueryKey.push(getAll.toString());
  }
  if (descOrder) {
    tempQueryKey.push(descOrder.toString());
  }
  return useQuery<IStoreLists[]>({
    queryKey: tempQueryKey,
    queryFn: async () => {
      const storeList = await getStoreForFeedbackLists(locationName, locationId, latitude, longitude, getAll, descOrder);
      return storeList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
  });
};

export const useGetAllLocationList = (options: UseGetAllLocationListOptions = {}) => {
  const { enabled = true, params = {} } = options;

  const isParamsEmpty = Object.keys(params).length === 0;

  return useQuery<LocationData>({
    queryKey: [QueryKeys.allLocation, params],
    queryFn: async () => {
      const locationList = await getAllLocationLists(params);
      return locationList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: enabled && !isParamsEmpty,
  });
};

export const useGetAllTicketList = () => {
  return useQuery<any>({
    queryKey: [QueryKeys.ticketList],
    queryFn: async () => {
      const ticketList = await getAllTicketLists();
      return ticketList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

const getPosition = () => {
  return new Promise<GeolocationPosition>((res, rej) => {
    navigator.geolocation.getCurrentPosition(res, rej);
  });
};

export const useMyLocation = () => {
  return useQuery<{
    latitude?: number;
    longitude?: number;
  }>({
    queryKey: [QueryKeys.myLocation],
    queryFn: async () => {
      let returnVal: {
        latitude?: number;
        longitude?: number;
      } = {
        latitude: undefined,
        longitude: undefined,
      };
      if (navigator.geolocation)
        try {
          const resolvedPosition = await getPosition();
          returnVal = { latitude: resolvedPosition.coords.latitude, longitude: resolvedPosition.coords.longitude };
        } catch (e) {
          console.log(e, 'position get error');
        }
      return returnVal;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: true,
  });
};

export const useGetAllExpressZones = (thanaId: string) => {
  return useQuery<any>({
    queryKey: [QueryKeys.expressZones, thanaId],
    queryFn: async () => {
      const ticketList = await getAllExpressZones(thanaId);
      return ticketList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: thanaId !== '-1' && !!thanaId,
  });
};

export const useGetAutoCompleteStoreList = (query: string, enabled?: boolean, limit?: number) => {
  const queryKey = createQueryKey(QueryKeys.storeAutoComplete, query, enabled, limit);

  return useQuery<IAutoCompleteStoreList>({
    queryKey,
    queryFn: async () => {
      const storeList = await getAutoCompleteStoreLists(query, limit);
      return storeList;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
    enabled: enabled,
  });
};
